import React, { useState, useEffect } from 'react';
import { REVIEWER_INFECTED_MESSAGE } from '../utils/scanStatus.mjs';

/**
 * A single supporting document, filling its container.
 *
 * Generalised from the PDF renderer embedded in expenseForm.mjs: same loading /
 * error / retry-by-remount behaviour and the iframe hash params that make a PDF
 * usable, plus the two things that renderer lacks - an image branch (receipts are
 * often photos, which an iframe shows without zoom or fit) and the graceful
 * "open in a new tab" fallback for anything the browser refuses to preview.
 *
 * Signed GCS URLs expire, so this component never caches one - it renders whatever
 * `url` it is handed and the caller re-fetches when needed.
 */
const DocumentPane = ({
  url,
  fileType = 'pdf',
  fileName,
  title,
  subtitle,
  signError = false,
  // The background virus scan flagged this file and the backend deleted it, so
  // there is no url to show. Say why instead of the generic "could not be
  // retrieved" copy, which would send the reviewer chasing a storage fault.
  infected = false,
}) => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [retryCount, setRetryCount] = useState(0);

  // A new document means a new load cycle - without this the spinner state from
  // the previous document leaks into the next one as the reviewer pages through.
  useEffect(() => {
    setLoading(true);
    setError(null);
    setRetryCount(0);
  }, [url]);

  const retry = () => {
    setRetryCount(c => c + 1);
    setLoading(true);
    setError(null);
  };

  const isImage = fileType === 'image';
  // An infected file must never be opened, even if a stale url is still around.
  const canOpen = Boolean(url) && !infected;

  const openInNewTab = () => {
    if (canOpen) window.open(url, '_blank', 'noopener,noreferrer');
  };

  const download = () => {
    if (!canOpen) return;
    const a = document.createElement('a');
    a.href = url;
    a.download = fileName || 'document';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
  };

  return (
    <div className="flex flex-col h-full bg-gray-900" data-print-exclude="true">
      {/* Toolbar */}
      <div className="bg-gray-800 text-white px-3 py-1.5 md:px-4 md:py-2.5 flex items-center justify-between gap-2 md:gap-3 flex-shrink-0">
        <div className="min-w-0">
          <p className="text-sm font-medium truncate">{title || fileName || 'Document'}</p>
          {subtitle && <p className="text-xs text-gray-400 truncate">{subtitle}</p>}
        </div>
        <div className="flex items-center gap-1.5 md:gap-2 flex-shrink-0">
          {error && (
            <button
              onClick={retry}
              className="min-h-[44px] md:min-h-0 px-2.5 md:px-3 py-1 text-xs bg-stanford-red hover:bg-stanford-light-red rounded transition-colors"
            >
              Retry
            </button>
          )}
          <button
            onClick={openInNewTab}
            disabled={!canOpen}
            className="min-h-[44px] md:min-h-0 px-2.5 md:px-3 py-1 text-xs bg-gray-700 hover:bg-gray-600 rounded transition-colors disabled:opacity-40"
          >
            Full Size
          </button>
          <button
            onClick={download}
            disabled={!canOpen}
            className="min-h-[44px] md:min-h-0 px-2.5 md:px-3 py-1 text-xs bg-gray-700 hover:bg-gray-600 rounded transition-colors disabled:opacity-40"
          >
            Download
          </button>
        </div>
      </div>

      {/* Phones: Android Chrome cannot draw a PDF inside a frame and iOS
          Safari shows only its first page, so point at the way that works. */}
      {canOpen && !signError && !isImage && (
        <p className="md:hidden flex-shrink-0 bg-gray-800 border-t border-gray-700 px-3 py-1.5 text-[11px] leading-snug text-gray-300">
          If the document does not show below, tap Full Size to open it.
        </p>
      )}

      {/* Body */}
      <div className="flex-1 relative overflow-hidden bg-gray-900">
        {/* The document could not be signed at all - a storage problem, not a
            preview problem, so say so rather than showing an empty frame. */}
        {infected && (
          <div className="absolute inset-0 flex flex-col items-center justify-center text-center px-6">
            <p className="text-red-400 font-medium">{REVIEWER_INFECTED_MESSAGE}</p>
          </div>
        )}

        {!infected && (!url || signError) && (
          <div className="absolute inset-0 flex flex-col items-center justify-center text-center px-6">
            <p className="text-gray-200 font-medium">This document could not be retrieved</p>
            <p className="text-gray-400 text-sm mt-1">
              The file is recorded on the claim but its storage link could not be generated.
            </p>
          </div>
        )}

        {canOpen && !signError && (
          <>
            {loading && (
              <div className="absolute inset-0 flex flex-col items-center justify-center">
                <div className="w-8 h-8 border-4 border-gray-700 border-t-stanford-red rounded-full animate-spin mb-3" />
                <p className="text-gray-400 text-sm">Loading document…</p>
              </div>
            )}

            {error && (
              <div className="absolute inset-0 flex flex-col items-center justify-center text-center px-6">
                <p className="text-gray-200 font-medium">Unable to preview this document inline</p>
                <p className="text-gray-400 text-sm mt-1 mb-4">
                  Your browser blocked the embedded preview. It will still open in a new tab.
                </p>
                <button
                  onClick={openInNewTab}
                  className="px-4 py-2 text-sm bg-stanford-red hover:bg-stanford-light-red text-white rounded-lg transition-colors"
                >
                  Open in New Tab
                </button>
              </div>
            )}

            {isImage ? (
              <div
                className="absolute inset-0 overflow-auto flex items-center justify-center p-2 md:p-4"
                style={{ display: loading || error ? 'none' : 'flex' }}
              >
                <img
                  key={`${url}-${retryCount}`}
                  src={url}
                  alt={fileName || 'Supporting document'}
                  className="max-w-full max-h-full object-contain"
                  onLoad={() => { setLoading(false); setError(null); }}
                  onError={() => { setError('Failed to load image'); setLoading(false); }}
                />
              </div>
            ) : (
              <iframe
                // Remounting on retry is what actually re-requests the document -
                // an iframe will not reload from a src it already holds.
                key={`${url}-${retryCount}`}
                src={`${url}#toolbar=1&navpanes=1&scrollbar=1&view=FitH&zoom=page-width`}
                className="absolute inset-0 w-full h-full border-0"
                title={fileName || 'Supporting document'}
                onLoad={() => { setLoading(false); setError(null); }}
                onError={() => { setError('Failed to load document'); setLoading(false); }}
                style={{ display: loading || error ? 'none' : 'block' }}
                scrolling="auto"
                allowFullScreen
              />
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default DocumentPane;
